<template>
  <VWidget
    :title="$t('Payments')"
    class="mb-4"
  >
    <div slot="widget-content">
      <v-layout
        row
        wrap
        pa-2
      >
        <v-flex
          lg12
          class="subheading py-2">
          {{ $t('Payment from') }}
          <router-link
            v-if="order.customer.customer_id"
            :to="{
              name: 'single_customer',
              params: { id: order.customer.customer_id }
            }"
          >
            {{ customerFullName }}
          </router-link>
          <template v-else>
            {{ customerFullName }}
          </template>

          <span
            v-if="order.payment.type === 'online'"
            class="font-weight-bold"
          >
            {{ $t('(online)') }}
          </span>
        </v-flex>
        <v-flex
          lg12
          py-2>
          <span class="font-weight-bold">{{ amountPaid | money }}</span>
          {{ $t('via') }}
          <span>{{ order.payment.name }}</span>
        </v-flex>
        <v-flex
          v-if="order.payment.type === 'online'"
          lg12
          py-2
          class="subheading mt-2">{{ $t('status:') }} <span class="font-weight-bold">{{ paymentStatus }}</span>
        </v-flex>

        <v-flex lg12>
          <v-layout pr-2>
            <v-spacer/>
            <v-btn
              v-if="canRefund"
              small
              class="error"
              @click="$emit('refund')">
              {{ $t('Refund') }}
            </v-btn>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="canVoid()"
                  :loading="voidInProgress"
                  :disabled="voidInProgress"
                  v-bind="attrs"
                  small
                  color="warning"
                  @click="voidOrder"
                  v-on="on"
                >
                  {{ $t('Void') }}
                </v-btn>
              </template>
              <span>{{ $t('Payment can usually be voided up to 24 hours after payment is made (may vary depending on payment provider).') }}</span>
            </v-tooltip>
            <v-btn
              v-if="canCapture"
              :loading="captureInProgress"
              :disabled="captureInProgress"
              small
              class="primary"
              @click="capturePayment()">
              {{ $t('Capture') }}
            </v-btn>
            <v-btn
              v-if="canMarkAsPaid"
              small
              class="primary"
              @click="$emit('mark-as-paid')">
              {{ $t('Mark as Paid') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
  </VWidget>
</template>

<script>
import { mapGetters } from 'vuex'
import Money from 'js-money'

export default {
  name: 'OrderSinglePaid',
  props: {
    order: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      voidInProgress: false,
      captureInProgress: false
    }
  },

  computed: {
    ...mapGetters(['currency', 'orderStateMachine']),

    customerFullName () {
      return `${this.order.customer.firstname} ${this.order.customer.lastname}`
    },

    paymentStatus () {
      if (!this.hasTransaction('payment') && this.hasTransaction('authorize')) {
        return 'authorized'
      }

      if (this.hasTransaction('authorize') && this.hasTransaction('payment') && this.order.hasPaymentCaptured()) {
        return 'captured'
      }

      if (this.hasTransaction('refund') && this.order.status !== 'refunded') {
        return 'partially refunded'
      }

      if (this.hasTransaction('refund') && this.order.status === 'refunded') {
        return 'refunded'
      }

      return 'authorized'
    },

    amountPaid () {
      if (this.hasTransaction('payment')) {
        return this.getTransaction('payment').amount
      }

      if (this.hasTransaction('authorize')) {
        return this.getTransaction('authorize').amount
      }

      return {
        amount: 0,
        currency: this.currency
      }
    },

    canCapture () {
      return this.order.payment.type === 'online' && !this.order.hasPaymentCaptured()
    },

    canMarkAsPaid () {
      const paymentEligible = this.order.payment.type === 'offline' ||
        (this.order.payment.type === 'online' && this.order.hasPendingAuthorization() && !this.order.hasPaymentCaptured())

      return paymentEligible &&
        this.order.status !== 'refunded' &&
        this.order.summary.total_refunded.amount === 0 &&
        this.order.summary.total_paid.amount < this.order.summary.total_due.amount &&
        this.can('bill')
    },

    canRefund () {
      return this.order.summary.total_paid.amount > 0 && this.hasRefundableItems && this.can('refund')
    },

    hasRefundableItems () {
      let hasRefundableItems = false
      let hasRefundableShipping = false

      this.order.items.forEach((item) => {
        if (!item.quantity_refunded || item.quantity_refunded < item.quantity) {
          hasRefundableItems = true
        }
      })

      if (this.order.shipping) {
        hasRefundableShipping = !(this.order.shipping.refunded || false)
      }

      return hasRefundableItems || hasRefundableShipping
    }
  },

  methods: {
    can (action) {
      return this.orderStateMachine && this.orderStateMachine.can(action, this.order.status)
    },

    async capturePayment () {
      if (this.captureInProgress) {
        return
      }

      this.captureInProgress = true

      const payment = this.getTransaction('authorize')
      if (!payment) {
        this.$toast.error(this.$t('There is no authorization that can be captured.'))
        this.captureInProgress = false
        return
      }

      try {
        const paymentResource = this.$http.make('payment')
        const response = await paymentResource.capture(payment.id)

        if (response.data.status !== 2) {
          this.$toast.error(this.$t(response.data.message))
          this.captureInProgress = false
          this.$radio.$emit('renew-order')

          return
        }

        this.$toast.success(this.$t('Payment captured'))
      } catch (error) {
        console.error(error)
      } finally {
        this.$radio.$emit('renew-order')

        this.captureInProgress = false
      }
    },

    hasTransaction (type) {
      if (!this.order.transactions || this.order.transactions.length === 0) {
        return false
      }

      return this.order.transactions.filter(trans => trans.type === type).length > 0
    },

    getTransaction (type) {
      if (this.hasTransaction(type)) {
        return this.order.transactions.find(trans => trans.type === type)
      }

      return null
    },

    async voidOrder () {
      if (this.voidInProgress) {
        return
      }

      const answer = await this.$root.$confirm(this.$t('Attention'), this.$t('Are you sure you want to void?'), { color: 'primary' })
      if (!answer) {
        return
      }

      this.voidInProgress = true

      this.$http.make('order').void(this.order)
        .then(() => {
          this.$toast.success(this.$t('Payment voided'))
          this.$radio.$emit('payment-voided')
        }).catch(err => {
          this.$toast.error(this.$t(err.message))
        }).finally(() => {
          this.voidInProgress = false
        })
    },

    canVoid () {
      const totalPaid = Money.fromInteger(this.order.summary.total_paid)
      const totalDue = Money.fromInteger(this.order.summary.total_due)

      return this.order.payment.type !== 'offline' &&
        !this.order.hasPaymentCapturedOffline() &&
        this.can('void') &&
        totalPaid.equals(totalDue)
    }
  }
}
</script>
